import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { plantMasterAPI, masterDataAPI } from "../services/api";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditPlantMaster.css";
import { getRoutePermissions } from "../utils/auth";

const normalizeCountry = (item) => ({
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  countryName: item.countryName || item.CountryName || item.COUNTRYNAME || "",
});

const normalizePlant = (item) => ({
  plantCode: item.plantCode || item.PlantCode || item.PLANTCODE || "",
  plantId: item.plantId || item.PlantId || item.PLANTID || "",
  plantName: item.plantName || item.PlantName || item.PLANTNAME || "",
  plantAddress: item.plantAddress || item.PlantAddress || item.PLANTADDRESS || "",
  city: item.city || item.City || item.CITY || "",
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  pinCode: item.pinCode || item.PinCode || item.PINCODE || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const AddEditPlantMaster = () => {
  const navigate = useNavigate();
  const { plantCode } = useParams();
  const isEditMode = !!plantCode;

  const routePermissions = getRoutePermissions("/plants");
  const { canAdd, canEdit } = routePermissions;
  const canSave = isEditMode ? canEdit : canAdd;

  const [plant, setPlant] = useState({
    plantCode: "",
    plantName: "",
    plantAddress: "",
    city: "",
    stateCode: "",
    countryCode: "",
    pinCode: "",
    isActive: true,
  });

  const [dropdowns, setDropdowns] = useState({
    countries: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const countrySelectRef = useRef(null);
  const stateSelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${isEditMode ? "edit" : "add"} plants.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/plants"));
      return;
    }
    loadDropdowns();
    if (isEditMode) {
      loadPlant();
    } else {
      loadNextPlantCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantCode, canSave]);


  // Initialize Select2 for Country dropdown
  useEffect(() => {
    if (dropdowns.countries.length === 0) {
      return;
    }

    if (countrySelectRef.current) {
      const $countrySelect = $(countrySelectRef.current);
      if ($countrySelect.data("select2")) {
        $countrySelect.select2("destroy");
      }
      $countrySelect.select2({
        width: "100%",
        placeholder: "Select Country",
        allowClear: false,
      });
      $countrySelect.on("select2:select", function (e) {
        const newCountryCode = e.params.data.id;
        handleChange("countryCode", newCountryCode);
        // Clear state when country changes
        handleChange("stateCode", "");
      });
      if (plant.countryCode) {
        $countrySelect.val(plant.countryCode).trigger("change");
      }
    }

    return () => {
      if (countrySelectRef.current) {
        const $select = $(countrySelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.countries]);


  // Update Select2 value when plant state changes
  useEffect(() => {
    if (countrySelectRef.current && dropdowns.countries.length > 0) {
      const $countrySelect = $(countrySelectRef.current);
      if ($countrySelect.data("select2")) {
        const currentVal = $countrySelect.val();
        if (currentVal !== (plant.countryCode || "")) {
          $countrySelect.val(plant.countryCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.countryCode]);

  const loadDropdowns = async () => {
    try {
      setLoading(true);
      const countryRes = await masterDataAPI.getCountries();

      setDropdowns({
        countries: (countryRes?.data || []).map((c) => normalizeCountry(c)),
        states: [],
      });
    } catch (e) {
      console.error("Error loading dropdowns:", e);
      setError("Failed to load dropdown data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };


  const loadNextPlantCode = async () => {
    try {
      const response = await plantMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setPlant((prev) => ({ ...prev, plantCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next plant code:", e);
      setPlant((prev) => ({ ...prev, plantCode: "PL00001" }));
    }
  };

  const loadPlant = async () => {
    try {
      setLoading(true);
      const response = await plantMasterAPI.getByCode(plantCode);
      if (response && response.success && response.data) {
        const plantData = normalizePlant(response.data);
        setPlant(plantData);
      } else {
        navigate("/plants");
      }
    } catch (e) {
      console.error("Error loading plant:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load plant details", "error");
      }
      navigate("/plants");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setPlant((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!plant.plantCode?.trim()) {
      Swal.fire("Error", "Plant Code is required", "error");
      return false;
    }
    if (!plant.plantId?.trim()) {
      Swal.fire("Error", "Plant ID is required", "error");
      return false;
    }
    if (!plant.plantName?.trim()) {
      Swal.fire("Error", "Plant Name is required", "error");
      return false;
    }
    if (!plant.countryCode) {
      Swal.fire("Error", "Country is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await plantMasterAPI.update(
          plantCode,
          plant,
          createdBy
        );
      } else {
        response = await plantMasterAPI.create(plant, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Plant saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/plants");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save plant",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving plant:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save plant. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/plants"
            onClick={(e) => {
              e.preventDefault();
              navigate("/plants");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Plant Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Plant" : "Add Plant"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/plants")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Country <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={countrySelectRef}
                  className="form-control select2-country"
                  required
                >
                  <option value="">Select Country</option>
                  {dropdowns.countries.map((c) => (
                    <option key={c.countryCode} value={c.countryCode}>
                      {c.countryName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4" style={{ display: 'none' }}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Plant Code <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={plant.plantCode}
                  required
                  fullWidth
                  disabled={isEditMode}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Plant ID <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={plant.plantId}
                  onChange={(e) => handleChange("plantId", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter plant ID"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Plant Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={plant.plantName}
                  onChange={(e) => handleChange("plantName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter plant name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">Plant Address</label>
                <TextField
                  value={plant.plantAddress}
                  onChange={(e) => handleChange("plantAddress", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter plant address"
                  multiline
                  rows={3}
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">City</label>
                <TextField
                  value={plant.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter city"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">Pin Code</label>
                <TextField
                  value={plant.pinCode}
                  onChange={(e) => handleChange("pinCode", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter pin code"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/plants")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !canSave}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditPlantMaster;



